// ABOUTME: ABC analysis tab of the stock page: picks per product over the previous calendar month with an A / B / C label.
// ABOUTME: Shows the current location, zone, location type and packstation zone range so fast movers can be placed closer to the packstation.
import { Box, useTheme } from "@mui/material";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import ShCopyButton from "../../../../../components/theme/ShCopy";
import Text from "../../../../../components/theme/text/Text";
import SidepanelLoader from '../../../../../components/theme/sidepanel2/SidepanelLoader';
import { useExportAbcAnalysisMutation, useGetAbcAnalysisQuery } from "../../../../../newapi/warehouse/stockSlice";
import { useGetWarehousesSelectQuery } from "../../../../../newapi/warehouse/warehouseApiSlice";
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { ABC_FILTER_KEYS } from '../../../../../components/global/DataGrid/Filter';
import MDynamicValues from '../../../../../components/global/Modals/MDynamicValues';
import MLoader from '../../../../../components/global/Modals/MLoader';
import { showToastMessage } from '../../../../../utils/toasts';

const SpStockDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));
const SPLocationDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpLocationDetails/SpLocationDetails"));

// Label colours: A is the fast mover, C the slow mover
const ABC_LABEL_VARIANTS = {
    A: "green",
    B: "orange",
    C: "grey"
};

// The backend analyses the previous full calendar month, the title names that month
const previousMonthLabel = (language) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return date.toLocaleDateString(language, { month: 'long', year: 'numeric' });
};

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    return (
        <ButtonGroup version={2}>
            <Shbutton
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                onClick={props.handleOpen}
                isLoading={props.isExporting}
                endIcon={<DownloadOutlinedIcon/>}
                variant="contained"
            >
                {t("export")}
            </Shbutton>
        </ButtonGroup>
    );
}

const AbcAnalysis = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();

    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    // The warehouse filter only makes sense with two or more active picking warehouses; the
    // select leaves bulk warehouses out
    const { data: warehouses } = useGetWarehousesSelectQuery();
    const showWarehouseFilter = (warehouses?.data?.length ?? 0) >= 2;
    const { data: relationData } = useGetRelationsSelectQuery();

    const [isExtended, setIsExtended] = useState(false);
    const [spStockLoading, setSpStockLoading] = useState();
    const [locationIsExtended, setLocationIsExtended] = useState(false);
    const [spLocationLoading, setSpLocationLoading] = useState();

    const gridOptions = {
        export: true,
        disableCustomActionsOnPhone: true,
        isSidepanel: true,
        filter: [
            ...(isFulfilment ? [ABC_FILTER_KEYS.RELATION] : []),
            ...(showWarehouseFilter ? [ABC_FILTER_KEYS.WAREHOUSE] : []),
            ABC_FILTER_KEYS.WAREHOUSE_ZONE,
            ABC_FILTER_KEYS.LOCATION_TYPE,
            ABC_FILTER_KEYS.LABEL
        ],
    };

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [loaderModalOpen, setLoaderModalOpen] = useState(false);

    const [exportAbcAnalysis, { isLoading: isExporting }] = useExportAbcAnalysisMutation();
    const handleExport = (values) => {
        if (isExporting) return;

        setLoaderModalOpen(true);
        exportAbcAnalysis({
            ...(values.relation ? { relationId: values.relation } : {}),
            ...(values.warehouse ? { warehouseId: values.warehouse } : {})
        }).unwrap()
            .then(() => {
                showToastMessage("success", t("export_ready"));
            })
            .catch((e) => {
                console.error(e);
                showToastMessage("error", t("export_failed"));
            })
            .finally(() => {
                setLoaderModalOpen(false);
            });
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions
                isExporting={isExporting}
                handleOpen={() => setExportModalOpen(true)}
            />
        });

        return () => {

        }
    }, [showWarehouseFilter]);

    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);

    const getProductDetails = (params) => {
        if (!params) return;

        const newProductId = parseInt(params.row.id);
        if (productIdRef.current !== newProductId) {
            if (!spStockLoading) {
                setSpStockLoading(true);
            }
            setProductId(newProductId);
            productIdRef.current = newProductId;
        }
        setIsExtended(true);
    };

    const [locationId, setLocationId] = useState();
    const locationIdRef = useRef(locationId);

    const getLocationDetails = (location) => {
        if (!location) return;

        const newLocationId = parseInt(location.id);
        if (locationIdRef.current !== newLocationId) {
            if (!spLocationLoading) {
                setSpLocationLoading(true);
            }
            setLocationId(newLocationId);
            locationIdRef.current = newLocationId;
        }
        setLocationIsExtended(true);
    };

    const columns = useMemo(() => [
        {
            field: "sku",
            headerName: t("sku"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton palette={theme.palette.mode === "dark" ? "pastel" : "normal"} variant={theme.palette.mode === "dark" ? "blue" : "primary"} />
                }
                return (
                    <Box sx={{
                        width: 1,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        ".copyel": {
                            opacity: 0,
                        },
                        "&:hover .copyel": {
                            opacity: 1,
                        }
                    }}>
                        <Text clickable semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{params.row.sku}</Text>
                        <ShCopyButton className={"copyel"} closeAfter={1500} value={params.row.sku}></ShCopyButton>
                    </Box>
                );
            }
        },
        {
            field: "description",
            headerName: t("description"),
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.description
            },
        },
        {
            field: "picks",
            headerName: t("picks"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.picks
            },
        },
        {
            field: "pieces",
            headerName: t("picked_pieces"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.pieces
            },
        },
        {
            field: "label",
            headerName: t("abc_label"),
            flex: 1,
            filter: ABC_FILTER_KEYS.LABEL,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShLabelSkeleton variant={"grey"} />
                }
                return (
                    <ShLabel size={32} fitted variant={ABC_LABEL_VARIANTS[params.row.label] ?? "grey"}>
                        {params.row.label}
                    </ShLabel>
                );
            },
        },
        ...isFulfilment ? [{
            field: "relation",
            headerName: t("relation"),
            flex: 1,
            filter: ABC_FILTER_KEYS.RELATION,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.relation?.name ?? t("unknown")
            },
        }] : [],
        ...showWarehouseFilter ? [{
            field: "warehouse",
            headerName: t("warehouse"),
            flex: 1,
            filter: ABC_FILTER_KEYS.WAREHOUSE,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.warehouse?.name ?? ""
            },
        }] : [],
        {
            field: "warehouseZone",
            headerName: t("warehouse_zone"),
            flex: 1,
            filter: ABC_FILTER_KEYS.WAREHOUSE_ZONE,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.warehouseZone?.description ?? ""
            },
        },
        {
            field: "warehouseLocationType",
            headerName: t("location_type"),
            flex: 1,
            filter: ABC_FILTER_KEYS.LOCATION_TYPE,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.warehouseLocationType?.description ?? ""
            },
        },
        {
            field: "location",
            headerName: t("location"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShLabelSkeleton variant={"yellow"} />
                }
                if (!params.row.warehouseLocation) {
                    return "";
                }
                const handleClick = (event) => {
                    event.stopPropagation();
                    getLocationDetails(params.row.warehouseLocation);
                };
                // A product on several locations shows its primary location and how many more there are
                const extraLocations = params.row.locationCount > 1 ? params.row.locationCount - 1 : 0;
                return (
                    <Box onClick={handleClick} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ShLabel clickable size={32} fitted variant={"yellow"}>
                            {params.row.warehouseLocation.location}
                        </ShLabel>
                        {extraLocations > 0 &&
                            <Text light>+{extraLocations}</Text>
                        }
                    </Box>
                )
            }
        },
        {
            field: "packstationZoneRange",
            headerName: t("packstation_zone_range"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return params.row.warehouseLocation?.packstationZoneRange ?? ""
            },
        },
    ], [theme.palette.mode, t, isFulfilment, showWarehouseFilter, spLocationLoading]);

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2
                key={theme.palette.mode}
                content={useGetAbcAnalysisQuery}
                title={`${t('abc_analysis')} ${previousMonthLabel(i18n.language)}`}
                gridOptions={gridOptions}
                gridActions={<GridActions handleOpen={() => setExportModalOpen(true)} isExporting={isExporting} />}
                onRowClick={getProductDetails}
                sortModel={{ field: "picks", type: "DESC" }}
                columns={columns}
            />

            <MDynamicValues
                open={exportModalOpen}
                handleClose={() => setExportModalOpen(false)}
                title={t("export_abc_analysis")}
                labelWidth={144}
                fields={[
                    ...(isFulfilment ? [{ name: "relation", label: t("relation"), type: "list", options: relationData, search: true }] : []),
                    ...(showWarehouseFilter ? [{ name: "warehouse", label: t("warehouse"), type: "list", options: warehouses?.data ?? [] }] : [])
                ]}
                onConfirm={(values) => handleExport(values)}
            />

            <MLoader
                open={loaderModalOpen}
                handleClose={() => setLoaderModalOpen(false)}
                message={t("preparing_export")}
            />

            {spStockLoading &&
                <SpStockDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    productId={productId}
                />
            }
            {spLocationLoading &&
                <SPLocationDetails
                    isExtended={locationIsExtended}
                    setIsExtended={setLocationIsExtended}
                    locationId={locationId}
                />
            }
        </Box>
    );
};

export default AbcAnalysis;
